// src/features/system/orchestrator/components/IncomesV2Panel.tsx
//
// Scope-based income definitions viewer + "New Income" trigger dialog.
// Reads from workflow_scopes (namespace='workflow.income') + outcome events,
// reusing the same infrastructure as the developer Scopes panel.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchWorkflowScopesApi,
  type WorkflowScope,
} from '@/features/system/orchestrator/api/scopesApi';
import {
  fetchScopeAttributes,
  type ScopeOutcomeAttribute,
} from '@/features/system/orchestrator/api/scopeOutcomesApi';
import {
  triggerWorkflowApi,
} from '@/features/system/orchestrator/api/workflowsApi';
import {
  fetchHandlersV2Api,
  type HandlerSpecRow,
} from '@/features/system/orchestrator/api/handlersV2Api';
import { fetchWorkersV2Api, type Worker } from '@/features/system/orchestrator/api/workersApi';
import AppDialog from '@/shared/components/ui/AppDialog';
import DarkDataTable from '@/shared/components/ui/DarkDataTable';
import MinimalisticTable, { type ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import { timeAgo } from '@/utils/date';
import { formatDate } from '@/features/system/orchestrator/components/handlerCatalogV2/createPresetV2';

const INCOME_NAMESPACE = 'workflow.income';
const INCOME_WORKFLOW_KEY = 'system.income.define';
const QK_INCOME_SCOPES = ['admin', 'incomesV2', 'scopes'] as const;

// ── Helpers ──────────────────────────────────────────────────────────────

function decodeScopeKey(raw: string): Record<string, string> | null {
  // Avoid the RegExp dotAll flag ("s") so we stay compatible with older TS targets.
  const m = raw.match(/^v\d+:([\s\S]+)$/);
  if (!m) return null;
  try {
    // PostgreSQL encode(…,'base64') wraps at 76 chars — strip whitespace.
    const json = atob(m[1].replace(/\s/g, ''));
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed?.fields)) return null;
    const map: Record<string, string> = {};
    for (const [k, v] of parsed.fields) {
      map[String(k)] = String(v ?? '');
    }
    return map;
  } catch {
    return null;
  }
}

function extractField(scope: WorkflowScope, field: string): string {
  const raw = scope.scope_key ?? scope.scopeKey ?? '';
  const decoded = decodeScopeKey(raw);
  return decoded?.[field] ?? '';
}

// ── Outcome attributes inline ────────────────────────────────────────────

function IncomeAttributes({ idScope }: { idScope: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'incomesV2', 'attributes', idScope] as const,
    queryFn: () => fetchScopeAttributes(idScope, { limit: 50 }),
    staleTime: 15_000,
    enabled: idScope > 0,
  });

  const attrs = useMemo(() => {
    const items: ScopeOutcomeAttribute[] = data?.items ?? [];
    const map: Record<string, any> = {};
    for (const item of items) {
      // Take first (latest) value per key.
      if (!(item.outcome_key in map)) {
        map[item.outcome_key] = item.value;
      }
    }
    return map;
  }, [data]);

  if (isLoading) return <div className="px-4 py-3 text-xs text-neutral-400">Loading attributes…</div>;
  if (error) return <div className="px-4 py-3 text-xs text-rose-400">Failed: {(error as any)?.message ?? String(error)}</div>;

  const keys = Object.keys(attrs);
  if (keys.length === 0) {
    return (
      <div className="px-4 py-3 text-xs text-neutral-500">
        No attributes found for this income definition yet. Use <span className="text-neutral-300">+ New</span> →
        <span className="text-neutral-300"> Define Income</span> and provide <span className="font-mono text-neutral-300">income_value</span>
        to store the value.
      </div>
    );
  }

  return (
    <div className="px-4 py-3">
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-xs">
        {keys.map((k) => (
          <React.Fragment key={k}>
            <span className="text-neutral-500 font-mono">{k}</span>
            <span className="text-neutral-200 font-mono break-all">
              {typeof attrs[k] === 'object' ? JSON.stringify(attrs[k]) : String(attrs[k] ?? '—')}
            </span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ── Main panel ───────────────────────────────────────────────────────────

type SortKey = 'handler' | 'input' | 'created';
type SortDir = 'asc' | 'desc';

// ── New Income dialog ────────────────────────────────────────────────────

const FIELD_CLS =
  'w-full pac-glass-input rounded px-2.5 py-1.5 text-xs text-neutral-200 placeholder-neutral-600 outline-none transition-colors font-mono';

const LABEL_CLS = 'block text-xs text-neutral-400 mb-1';

interface NewIncomeFormState {
  handler_key: string;
  input_key: string;
  description: string;
  income_value: string;
  value_kind: string;
  metadata: string;
}

const EMPTY_FORM: NewIncomeFormState = {
  handler_key: '',
  input_key: '',
  description: '',
  income_value: '',
  value_kind: '',
  metadata: '',
};

function NewIncomeDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<NewIncomeFormState>({ ...EMPTY_FORM });
  const [lastResult, setLastResult] = useState<{ run_id: string; ok: boolean } | null>(null);
  const [handlerDropOpen, setHandlerDropOpen] = useState(false);
  const handlerInputRef = useRef<HTMLInputElement | null>(null);
  const handlerDropRef = useRef<HTMLDivElement | null>(null);

  // Fetch handlers + workers to match the Handler Catalog table layout.
  const { data: handlersData } = useQuery({
    queryKey: ['admin', 'incomesV2', 'handlersV2'] as const,
    queryFn: () => fetchHandlersV2Api({ limit: 500 }),
    staleTime: 60_000,
    enabled: open,
  });

  const { data: workersData } = useQuery({
    queryKey: ['admin', 'incomesV2', 'workersV2'] as const,
    queryFn: () => fetchWorkersV2Api({ limit: 500 }),
    staleTime: 60_000,
    enabled: open,
  });

  const workerByKey = useMemo<Record<string, Worker>>(() => {
    const items = workersData?.items ?? [];
    const map: Record<string, Worker> = {};
    for (const w of items) {
      const k = String(w.worker_key ?? '').trim();
      if (k) map[k] = w;
    }
    return map;
  }, [workersData]);

  const handlersSorted = useMemo(() => {
    const items: HandlerSpecRow[] = (handlersData?.items ?? []).slice();
    items.sort((a, b) => String(a.handler_key).toLowerCase().localeCompare(String(b.handler_key).toLowerCase()));
    return items;
  }, [handlersData]);

  const handlerColumns: ColumnDef<HandlerSpecRow>[] = useMemo(() => [
    {
      header: 'Handler',
      accessor: 'handler_key',
      Cell: (value: string | undefined) => (
        <span className="font-mono text-xs text-neutral-200">{String(value ?? '—')}</span>
      ),
    },
    {
      header: 'Worker',
      accessor: 'worker_key',
      Cell: (value: string | undefined) => {
        const wk = String(value ?? '').trim();
        const worker = wk ? workerByKey[wk] : undefined;
        if (!worker) return <span className="text-xs text-neutral-300">{wk || '—'}</span>;
        return (
          <div className="flex flex-col text-xs">
            <span className="text-neutral-200">{worker.worker_key}</span>
            <span className="text-neutral-400">{worker.transport}</span>
          </div>
        );
      },
    },
    {
      header: 'Status',
      accessor: 'is_active',
      Cell: (value: boolean | undefined) => (
        <span className={value ? 'text-emerald-400 text-xs' : 'text-neutral-500 text-xs'}>
          {value ? 'Active' : 'Disabled'}
        </span>
      ),
    },
    {
      header: 'Updated',
      accessor: 'updated_at',
      Cell: (value: string | undefined) => {
        const ts = String(value ?? '').trim();
        if (!ts) return <span className="text-xs text-neutral-500">—</span>;
        return (
          <span title={formatDate(ts)} className="text-xs text-neutral-300">
            {timeAgo(ts)}
          </span>
        );
      },
    },
  ], [workerByKey]);

  useEffect(() => {
    if (!handlerDropOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (handlerInputRef.current?.contains(target)) return;
      if (handlerDropRef.current?.contains(target)) return;
      setHandlerDropOpen(false);
    };
    window.addEventListener('mousedown', onMouseDown);
    return () => window.removeEventListener('mousedown', onMouseDown);
  }, [handlerDropOpen]);

  const handlerKeyBase = useCallback((row: HandlerSpecRow): string => {
    const base = String(row.handler_key_base ?? '').trim();
    if (base) return base;
    const hk = String(row.handler_key ?? '').trim();
    return hk
      .replace(/:latest$/i, '')
      .replace(/:v\d+$/i, '')
      .replace(/\.v\d+$/i, '');
  }, []);

  const set = useCallback(
    <K extends keyof NewIncomeFormState>(key: K, val: NewIncomeFormState[K]) =>
      setForm((prev) => ({ ...prev, [key]: val })),
    [],
  );

  const triggerMut = useMutation({
    mutationFn: async () => {
      // Build inputs, only include non-empty fields.
      const inputs: Record<string, unknown> = {
        handler_key: form.handler_key.trim(),
        input_key: form.input_key.trim(),
      };
      if (form.description.trim()) inputs.description = form.description.trim();
      if (form.value_kind.trim()) inputs.value_kind = form.value_kind.trim();

      // income_value: try parse as JSON, fall back to plain string.
      if (form.income_value.trim()) {
        try {
          inputs.income_value = JSON.parse(form.income_value.trim());
        } catch {
          inputs.income_value = form.income_value.trim();
        }
      }

      // metadata: must be valid JSON object if provided.
      if (form.metadata.trim()) {
        inputs.metadata = JSON.parse(form.metadata.trim()); // let throw if invalid
      }

      return triggerWorkflowApi({
        workflow_key: INCOME_WORKFLOW_KEY,
        inputs,
      });
    },
    onSuccess: (res) => {
      setLastResult({ run_id: res.workflow_run_id, ok: true });
      setForm({ ...EMPTY_FORM });
      // Invalidate scope list after a short delay so the step has time to complete.
      setTimeout(() => qc.invalidateQueries({ queryKey: [...QK_INCOME_SCOPES] }), 3000);
    },
    onError: () => {
      setLastResult({ run_id: '', ok: false });
    },
  });

  const canSubmit = form.handler_key.trim().length > 0 && form.input_key.trim().length > 0;

  const handleConfirm = () => {
    setLastResult(null);
    triggerMut.mutate();
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) {
      // Reset on close.
      setForm({ ...EMPTY_FORM });
      setLastResult(null);
    }
    onOpenChange(v);
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Define Income"
      description="Trigger system.income.define workflow to register a new income definition."
      size="md"
      onConfirm={handleConfirm}
      confirmLabel="Run Workflow"
      confirmDisabled={!canSubmit}
      isBusy={triggerMut.isPending}
    >
      <div className="space-y-3">
        {/* handler_key */}
        <div className="relative">
          <label className={LABEL_CLS}>
            handler_key <span className="text-rose-400">*</span>
          </label>
          <input
            ref={handlerInputRef}
            className={FIELD_CLS}
            placeholder="Select handler…"
            value={form.handler_key}
            readOnly
            onMouseDown={(e) => {
              // Keep focus stable and open the picker.
              e.preventDefault();
              setHandlerDropOpen(true);
            }}
          />
          {handlerDropOpen && (
            <div
              ref={handlerDropRef}
              className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-auto rounded pac-glass-input shadow-lg"
            >
              <MinimalisticTable<HandlerSpecRow>
                columns={handlerColumns}
                data={handlersSorted}
                noDataMessage="No handler entries."
                showSearch
                searchPlaceholder="Search handlers…"
                showNewButton={false}
                onRowClick={(row) => {
                  const base = handlerKeyBase(row);
                  set('handler_key', base);
                  setHandlerDropOpen(false);
                }}
              />
              <div className="px-3 py-2 text-[10px] text-neutral-500 border-t border-neutral-800">
                Selecting a row stores the <span className="font-mono">base handler key</span> (without version).
              </div>
            </div>
          )}
        </div>

        {/* input_key */}
        <div>
          <label className={LABEL_CLS}>
            input_key <span className="text-rose-400">*</span>
          </label>
          <input
            className={FIELD_CLS}
            placeholder="e.g. url_reaper_template"
            value={form.input_key}
            onChange={(e) => set('input_key', e.target.value)}
          />
        </div>

        {/* description */}
        <div>
          <label className={LABEL_CLS}>description</label>
          <input
            className={FIELD_CLS}
            placeholder="Human-readable description"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </div>

        {/* income_value */}
        <div>
          <label className={LABEL_CLS}>income_value <span className="text-neutral-600 text-[10px]">jsonb — parsed as JSON or stored as string</span></label>
          <textarea
            rows={2}
            className={FIELD_CLS + ' resize-y'}
            placeholder='e.g. "https://..." or {"key":"val"} or 42'
            value={form.income_value}
            onChange={(e) => set('income_value', e.target.value)}
          />
        </div>

        {/* value_kind */}
        <div>
          <label className={LABEL_CLS}>value_kind <span className="text-neutral-600 text-[10px]">auto-detected if omitted</span></label>
          <select
            className={FIELD_CLS}
            value={form.value_kind}
            onChange={(e) => set('value_kind', e.target.value)}
          >
            <option value="">— auto-detect —</option>
            <option value="string">string</option>
            <option value="int">int</option>
            <option value="float">float</option>
            <option value="bool">bool</option>
            <option value="jsonb">jsonb</option>
            <option value="timestamp">timestamp</option>
          </select>
        </div>

        {/* metadata */}
        <div>
          <label className={LABEL_CLS}>metadata <span className="text-neutral-600 text-[10px]">JSON object — validation, UI hints, etc.</span></label>
          <textarea
            rows={2}
            className={FIELD_CLS + ' resize-y'}
            placeholder='e.g. {"required": true, "ui_widget": "url"}'
            value={form.metadata}
            onChange={(e) => set('metadata', e.target.value)}
          />
        </div>

        {/* Result banner */}
        {lastResult && (
          <div className={`text-xs rounded px-3 py-2 ${lastResult.ok ? 'bg-emerald-900/40 text-emerald-300' : 'bg-rose-900/40 text-rose-300'}`}>
            {lastResult.ok
              ? <>Workflow triggered — run <span className="font-mono">{lastResult.run_id}</span>. Scope will appear shortly.</>
              : <>Failed to trigger workflow. Check console for details.</>}
          </div>
        )}

        {triggerMut.error && !lastResult?.ok && (
          <div className="text-xs text-rose-400">
            {(triggerMut.error as any)?.message ?? String(triggerMut.error)}
          </div>
        )}
      </div>
    </AppDialog>
  );
}

export default function IncomesV2Panel() {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);

  const { data: scopesRaw = [], isLoading, error } = useQuery<WorkflowScope[], Error>({
    queryKey: [...QK_INCOME_SCOPES],
    queryFn: async () => {
      const res = await fetchWorkflowScopesApi({ scope_namespace: INCOME_NAMESPACE, limit: 500 });
      return (res?.items ?? res ?? []) as WorkflowScope[];
    },
    staleTime: 30_000,
  });

  // Enrich each scope with decoded handler_key / input_key.
  const enriched = useMemo(() =>
    scopesRaw.map((s) => ({
      scope: s,
      handlerKey: extractField(s, 'handler_key'),
      inputKey: extractField(s, 'input_key'),
    })),
    [scopesRaw],
  );

  // Filter.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return enriched;
    return enriched.filter(
      (e) =>
        e.handlerKey.toLowerCase().includes(term) ||
        e.inputKey.toLowerCase().includes(term),
    );
  }, [enriched, search]);

  // Sort.
  const sorted = useMemo(() => {
    const mul = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'handler') cmp = a.handlerKey.localeCompare(b.handlerKey);
      else if (sortKey === 'input') cmp = a.inputKey.localeCompare(b.inputKey);
      else {
        const ta = new Date(a.scope.created_at ?? '').getTime() || 0;
        const tb = new Date(b.scope.created_at ?? '').getTime() || 0;
        cmp = ta - tb;
      }
      return cmp * mul;
    });
  }, [filtered, sortKey, sortDir]);

  if (isLoading) return <div className="p-4 text-sm text-neutral-400">Loading income definitions…</div>;
  if (error) return <div className="p-4 text-sm text-rose-400">Failed: {error.message}</div>;

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const indicator = (key: SortKey) =>
    sortKey !== key ? '' : sortDir === 'asc' ? ' ▲' : ' ▼';

  const rowKey = (e: (typeof enriched)[number], i: number) =>
    `${e.handlerKey}::${e.inputKey}::${i}`;

  const uniqueKey = (e: (typeof enriched)[number]) =>
    `${e.handlerKey}::${e.inputKey}`;

  return (
    <div className="h-full min-h-0 flex flex-col">
      {/* New Income dialog */}
      <NewIncomeDialog open={newOpen} onOpenChange={setNewOpen} />

      {/* Header bar */}
      <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-sm text-neutral-200">Income Definitions</span>
          <span className="text-xs text-neutral-500">
            namespace: <span className="font-mono text-neutral-400">{INCOME_NAMESPACE}</span>
          </span>
          <span className="text-xs text-neutral-500">{filtered.length} entries</span>
          <span className="text-xs text-neutral-600">
            Click a row to view <span className="font-mono">income_value</span>.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setNewOpen(true)}
            className="px-2.5 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-xs text-white transition-colors"
          >
            + New
          </button>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by handler or input key…"
            className="w-64 pac-glass/60 rounded px-2 py-1 text-xs text-neutral-200 placeholder-neutral-600 outline-none focus:border-neutral-500 transition-colors"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {sorted.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <span className="text-sm text-neutral-500">
              {search ? 'No matching entries.' : 'No income definitions found. Run migration 0037 to seed data from legacy input_definitions.'}
            </span>
          </div>
        ) : (
          <DarkDataTable
            columns={[
              {
                header: (
                  <button type="button" onClick={() => toggleSort('handler')}
                    className={'inline-flex items-center gap-1 whitespace-nowrap ' +
                      (sortKey === 'handler' ? 'text-neutral-300' : 'text-neutral-500 hover:text-neutral-300')}>
                    Handler Key{indicator('handler')}
                  </button>
                ),
                tdClassName: 'text-neutral-200 font-mono text-xs',
                render: (e) => <span className="truncate max-w-[300px] block" title={e.handlerKey}>{e.handlerKey || '—'}</span>,
              },
              {
                header: (
                  <button type="button" onClick={() => toggleSort('input')}
                    className={'inline-flex items-center gap-1 whitespace-nowrap ' +
                      (sortKey === 'input' ? 'text-neutral-300' : 'text-neutral-500 hover:text-neutral-300')}>
                    Input Key{indicator('input')}
                  </button>
                ),
                tdClassName: 'text-neutral-200 font-mono text-xs',
                render: (e) => <span className="truncate max-w-[250px] block" title={e.inputKey}>{e.inputKey || '—'}</span>,
              },
              {
                header: (
                  <button type="button" onClick={() => toggleSort('created')}
                    className={'inline-flex items-center gap-1 whitespace-nowrap ' +
                      (sortKey === 'created' ? 'text-neutral-300' : 'text-neutral-500 hover:text-neutral-300')}>
                    Created{indicator('created')}
                  </button>
                ),
                thClassName: 'whitespace-nowrap',
                tdClassName: 'text-neutral-500 whitespace-nowrap text-xs',
                render: (e) => {
                  const ts = e.scope.created_at;
                  if (!ts) return '—';
                  return <span title={ts}>{timeAgo(ts)}</span>;
                },
              },
              {
                header: <span className="text-neutral-500">id_scope</span>,
                thClassName: 'whitespace-nowrap',
                tdClassName: 'text-neutral-600 font-mono text-[10px]',
                render: (e) => String(e.scope.id_scope ?? '—'),
              },
            ]}
            rows={sorted}
            getRowKey={rowKey}
            emptyMessage="No income definitions."
            frame={false}
            onRowClick={(e) => {
              const k = uniqueKey(e);
              setExpandedKey((prev) => (prev === k ? null : k));
            }}
            isRowSelected={(e) => uniqueKey(e) === expandedKey}
            isRowExpanded={(e) => uniqueKey(e) === expandedKey}
            renderExpandedRow={(e) => {
              const id = e.scope.id_scope;
              if (id == null) return <div className="px-4 py-3 text-xs text-neutral-500">No id_scope.</div>;
              return <IncomeAttributes idScope={id} />;
            }}
          />
        )}
      </div>
    </div>
  );
}
